'use client'

import { twMerge } from '@/lib/classname/twMerge'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { type ValidExtensions } from '@/lib/utils/upload'
import { Button } from '@/ui/buttons/button/Button.client'
import { cva } from 'class-variance-authority'
import { DropZone as AriaDropZone, FileTrigger } from 'react-aria-components'
import { DropZoneContent, DropZoneFieldGroup } from './_components/DropZoneField.client'
import { useDropZoneFieldState } from './_hooks/useDropZoneFieldState'
import type { DropZoneValidationError } from './_hooks/useDropZoneFiles'
import { useDropZoneFiles } from './_hooks/useDropZoneFiles'
import { UploadedFileItemFromAttachment } from './uploaded-file-item/UploadedFileItem.client'

export type DropZoneProps = UiComponentProps<{
  /** Currently attached files. This component is fully controlled */
  files: File[]
  /** Called with the next file list on add and on delete */
  onFilesChange: (files: File[]) => void
  /** Extensions accepted by the file picker and enforced on drop */
  validExtensions: ValidExtensions[]
  allowsMultiple?: boolean
  isLoading?: boolean
  isRequired?: boolean
  isDisabled?: boolean
  /** Error message rendered under the field. The caller owns the error state */
  errorMessage?: string
  /** Fires when a batch is rejected, with a ready-to-display message */
  onValidationError?: (error: DropZoneValidationError) => void
  /** Bytes still available, shown in the formats hint and enforced on add */
  remainingSize: number
}>

const interactiveSurface =
  'border-dashed border-brand-2-200 bg-white data-[drop-target]:border-brand-2-400 data-[hovered]:border-brand-2-400 data-[drop-target]:bg-brand-2-50 data-[hovered]:bg-brand-2-50'

const dropZoneSurfaceCva = cva(
  'flex w-full flex-col items-center gap-2 rounded-sm border-px px-2 py-3 transition-colors data-[focus-visible]:border-solid data-[focus-visible]:border-brand-1-700 data-[focus-visible]:outline data-[focus-visible]:outline-2 data-[focus-visible]:outline-offset-2 data-[focus-visible]:outline-brand-1-700 hd:border-1.5',
  {
    variants: {
      status: {
        default: interactiveSurface,
        progress: interactiveSurface,
        error: 'border-solid border-error-fg bg-white',
        disabled:
          'border-dashed border-brand-2-400 bg-brand-2-50 data-[hovered]:border-brand-2-400',
      },
    },
    defaultVariants: {
      status: 'default',
    },
  },
)

export function DropZone({
  id,
  className,
  'data-testid': dataTestId = 'dropzone',
  files,
  onFilesChange,
  isDisabled,
  isRequired,
  validExtensions,
  isLoading,
  allowsMultiple = false,
  errorMessage,
  onValidationError,
  remainingSize,
}: DropZoneProps) {
  const { t } = useTranslation(['dropzone'])

  const field = useDropZoneFieldState({ isLoading, isDisabled, errorMessage })
  const fileHandlers = useDropZoneFiles({
    files,
    onFilesChange,
    validExtensions,
    allowsMultiple,
    remainingSize,
    onValidationError,
  })

  const uploadingLabel = t('dropzone:uploading')

  return (
    <div
      id={id}
      data-testid={dataTestId}
      className={twMerge(className, 'flex w-full flex-col gap-2')}
    >
      <DropZoneFieldGroup
        disabled={field.isFieldDisabled}
        errorId={field.errorId}
        shouldDisplayError={field.shouldDisplayError}
        errorMessage={errorMessage}
      >
        <AriaDropZone
          data-testid={`${dataTestId}/area`}
          data-status={field.status}
          aria-labelledby={field.labelId}
          aria-describedby={field.errorId || undefined}
          getDropOperation={fileHandlers.getDropOperation}
          isDisabled={field.isFieldDisabled}
          onDrop={fileHandlers.onDrop}
          className={dropZoneSurfaceCva({ status: field.status })}
        >
          <DropZoneContent
            label={t('dropzone:label')}
            isRequired={isRequired}
            formatsHint={t('dropzone:placeholder', {
              formats: validExtensions.join(', '),
              size: remainingSize,
            })}
            status={field.status}
            labelId={field.labelId}
            uploadingLabel={uploadingLabel}
            progressAriaLabel={uploadingLabel}
            showProgress={field.fakeProgress.showProgress}
            progress={field.fakeProgress.progress}
            isProgressFadingOut={field.fakeProgress.isFadingOut}
          >
            <FileTrigger
              allowsMultiple={allowsMultiple}
              onSelect={fileHandlers.onSelect}
              acceptedFileTypes={fileHandlers.acceptedFileMimes}
            >
              <Button
                variant="secondary"
                isDisabled={field.isFieldDisabled}
                aria-label={t('dropzone:cta')}
              >
                {t('dropzone:cta')}
              </Button>
            </FileTrigger>
          </DropZoneContent>
        </AriaDropZone>
      </DropZoneFieldGroup>
      {!!files.length && (
        <div className="flex flex-col gap-1" role="list">
          {files.map((file) => (
            <div key={`${file.name}-${file.size}`} role="listitem" className="w-full">
              <UploadedFileItemFromAttachment
                item={file}
                onDelete={fileHandlers.onDelete}
                isDisabled={field.isFieldDisabled}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
